import React, { createContext, useContext, useRef, useState } from 'react';

import { useTableScrollDimensions } from './useTableScrollDimensions';
import { useTableScrollListeners } from './useTableScrollListeners';

interface TableScrollerContextProps {
  syncScrollRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  clientWidth: number;
  scrollWidth: number;
  handleAddRefToSyncScroll: (element: HTMLDivElement) => void;
  handleRemoveRefFromSyncScroll: (element: HTMLDivElement) => void;
  handleAddPrimaryRefToObserve: (element: HTMLDivElement) => void;
  isScrolling?: boolean;
  hasHorizontalOverflow: boolean;
}

const TableScrollerContext = createContext<TableScrollerContextProps | undefined>(undefined);

export const TableScrollerProvider: React.FC<{
  children: React.ReactNode;
  remeasureKey?: string;
}> = ({ children, remeasureKey }) => {
  const [clientWidth, setClientWidth] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const [primaryElement, setPrimaryElement] = useState<HTMLDivElement | null>(null);
  const primaryRef = useRef<HTMLDivElement | null>(null);
  const syncScrollRefs = useRef<(HTMLDivElement | null)[]>([]);

  const hasHorizontalOverflow = scrollWidth > clientWidth;
  const handleAddPrimaryRefToObserve = (element: HTMLDivElement) => {
    primaryRef.current = element;
    setPrimaryElement(element);
  };
  const handleAddRefToSyncScroll = (element: HTMLDivElement) => {
    if (element && !syncScrollRefs.current.includes(element)) {
      element.scrollLeft = primaryRef.current?.scrollLeft ?? 0;
      syncScrollRefs.current.push(element);
    }
  };
  const handleRemoveRefFromSyncScroll = (element: HTMLDivElement) => {
    syncScrollRefs.current = syncScrollRefs.current.filter((ref) => ref !== element);
  };

  useTableScrollListeners({ primaryRef, syncScrollRefs, setIsScrolling });
  useTableScrollDimensions({ primaryElement, setClientWidth, setScrollWidth, remeasureKey });

  return (
    <TableScrollerContext.Provider
      value={{
        syncScrollRefs,
        clientWidth,
        scrollWidth,
        handleAddRefToSyncScroll,
        handleRemoveRefFromSyncScroll,
        handleAddPrimaryRefToObserve,
        isScrolling,
        hasHorizontalOverflow,
      }}
    >
      {children}
    </TableScrollerContext.Provider>
  );
};

export const useTableScrollerContext = () => {
  const context = useContext(TableScrollerContext);
  if (!context) {
    throw new Error('useTableScrollerContext must be used within a TableScrollerProvider');
  }
  return context;
};
